import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import React, { useContext, useReducer, createContext } from 'react';

import { Alert, Snackbar, Typography } from '@mui/material';

import useResponsiveScreen from './useResponsiveScreen';

const SnackbarContext = createContext({
  showToast: () => undefined,
  hideToast: () => undefined,
  isVisible: false,
});

const { Provider, Consumer: SnackbarConsumer } = SnackbarContext;

const reducer = (state, action) => {
  switch (action.type) {
    case 'openToast': {
      // Renamed 'props' to 'toastProps' to satisfy the linter
      const { message, severity, verticalPos, horizontalPos, props = {} } = action;
      return {
        ...state,
        message,
        severity,
        verticalPos,
        horizontalPos,
        toastProps: props,
        isVisible: true,
      };
    }
    case 'hideToast':
      return {
        ...state,
        message: null,
        verticalPos: null,
        horizontalPos: null,
        toastProps: {},
      };
    default:
      throw new Error('Unspecified reducer action');
  }
};

const SnackbarProvider = ({ children }) => {
  const initialState = {
    message: null,
    severity: null,
    verticalPos: null,
    horizontalPos: null,
    toastProps: {}, // Renamed
  };

  const [state, dispatch] = useReducer(reducer, initialState);
  const { message, severity, verticalPos, horizontalPos, toastProps } = state;
  const isVisible = !!message;
  const { isSmallScreen } = useResponsiveScreen();

  const showToast = (modal) => {
    dispatch({ type: 'openToast', ...modal });
  };

  const hideToast = () => {
    // Safety check using the renamed variable name
    if (toastProps?.onClose) {
      toastProps.onClose();
    }
    dispatch({ type: 'hideToast' });
  };

  return (
    <Provider value={{ ...state, showToast, hideToast, isVisible }}>
      {children}
      <Snackbar
        key={uuidv4()}
        open={isVisible}
        anchorOrigin={{
          vertical: verticalPos || 'bottom',
          horizontal: horizontalPos || 'center',
        }}
        autoHideDuration={5000}
        onClose={hideToast}
      >
        <Alert onClose={hideToast} severity={severity || 'info'} sx={{ width: '100%' }}>
          <Typography
            variant="subtitle1"
            sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem' }}
          >
            {message}
          </Typography>
        </Alert>
      </Snackbar>
    </Provider>
  );
};

const useToast = () => useContext(SnackbarContext);

SnackbarProvider.propTypes = {
  children: PropTypes.node,
};

export { useToast, SnackbarConsumer, SnackbarProvider };
